import { Component, Input, OnInit, SimpleChanges } from '@angular/core';
import { Browser } from '@capacitor/browser';
import { LoadingController, ModalController, ToastController } from '@ionic/angular';
import { Account, SearchDocumentsRequest } from 'src/models/account.model';
import { SecureDocumentPopoverComponent } from './secure-document-popover/secure-document-popover.component';
import { UsersService } from 'src/services/users.service';
import { AlertMessager } from 'src/libs/alertMessager';
import { AccountDocument, DocumentType } from 'src/models/account-document.model';
import { DocumentService } from 'src/services/document.service';
import { Organization } from 'src/models/organization';
import { OrganizationRequiredDocument } from 'src/models/organization-required-document';
import { Subscription } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { RodeoService } from 'src/services/rodeo.service';
import { RegistrationChangeRequest, RegistrationChangeRequestDocument } from 'src/models/registration-change-request';
import { FormGroup, FormArray, FormControl } from '@angular/forms';

@Component({
  selector: 'app-secure-documents',
  templateUrl: './secure-documents.component.html',
  styleUrls: ['./secure-documents.component.scss'],
})
export class SecureDocumentsComponent implements OnInit {
  @Input() account: Account;
  @Input() location: string = 'account';
  @Input() requestDocuments?: RegistrationChangeRequestDocument[];
  @Input() changeRequest?: RegistrationChangeRequest;
  @Input() changeRequestForm: FormGroup;

  isLoading = false;

  popoverActive = false;

  docType = DocumentType;
  hasMissingDocs = false;
  displayDocumentsMap: Map<number, {
    showTitle: boolean,
    title: string, // Organization Name or Other Documents
    org?: Organization,
    requiredDocs?: OrganizationRequiredDocument[],
    showDocs: boolean,
    missingDocs?: AccountDocument[],
    uploadedDocs?: AccountDocument[]
  }> = new Map();

  subscriptions: Subscription[] = [];

  orgId: number;

  get displayDocumentsArray() {
    return Array.from(this.displayDocumentsMap.values());
  }

  constructor(
    private modalCtrl: ModalController,
    private userService: UsersService,
    private alert: AlertMessager,
    private documentService: DocumentService,
    private loadingCtrl: LoadingController,
    private route: ActivatedRoute,
    private rodeoService: RodeoService,
    private alertMessager: AlertMessager
  ) { }
  async ngOnInit() {
    this.isLoading = true;

    const loader = await this.loadingCtrl.create({
      message: 'Loading documents...',
    });

    await loader.present();

    this.subscriptions.push(
      this.route.queryParamMap.subscribe(async (params) => {
        if (params.has('rodeoId')) {
          const rodeoId = Number(params.get('rodeoId'));
          const rodeo = await this.rodeoService.getRodeo(rodeoId);

          this.orgId = rodeo.organization.id;
        } else if (params.has('organizationId')) {
          this.orgId = Number(params.get('organizationId'));
        }

        console.log('orgId: ', this.orgId);
        await this.initDocMap();

        this.isLoading = false;
        await loader.dismiss();
      })
    );
  }

  ionViewWillLeave() {
    this.subscriptions.forEach(x => x.unsubscribe());
    this.subscriptions = [];
  }

  
  async showIrsTaxDoc(doctype: 'W9' | 'W8') {
    if (doctype === 'W9') {
      await Browser.open({
        url: 'https://www.irs.gov/pub/irs-pdf/fw9.pdf'
      });
    } else {
      await Browser.open({
        url: 'https://www.irs.gov/pub/irs-pdf/fw8ben.pdf'
      });
    }
  }

  async showDoc(document: AccountDocument) {
    if (this.popoverActive) {
      return;
    }

    await this.openDocModal(document);
  }

  async openDocModal(document: AccountDocument) {
    this.popoverActive = true;

    const modal = await this.modalCtrl.create({
      component: SecureDocumentPopoverComponent,
      cssClass: 'secure-document-modal',
      componentProps: {
        viewImageDoc: document,
        account: this.account
      }
    });

    await modal.present();

    const { data } = await modal.onDidDismiss();
    this.popoverActive = false;

    if (data) {
      const loader = await this.loadingCtrl.create({
        message: 'Updating account...',
      });
  
      await loader.present();

      try {
        this.account = await this.userService.getAccount(this.account.id);
        await this.initDocMap();
        this.alertMessager.showToast(undefined, 'Account updated successfully!', 'success');
        await loader.dismiss();
      } catch (err) {
        console.log(err);
        this.alertMessager.showToast(undefined, 'Could not update account', 'danger');
        await loader.dismiss();
      }
    }
  }

  async removeDoc(document: AccountDocument) {
    // console.log('fileOption to remove:', fileOption, 'at index', index);

    // TODO: Cases for Horse Files (Papers, Coggins)
    const confirm = await this.alert.prompt(
      'Remove Document',
      'Are you sure you want to remove this document?');
    
    if (!confirm) return;

    const loader = await this.loadingCtrl.create({
      message: 'Removing document...',
    });

    await loader.present();

    // TODO: Cases for Change Request Doc (ChangeRequest) and Horse Files (Papers, Coggins)
    try {
     
      await this.documentService.deleteAccountDocuments([document.id]);
      this.account = await this.userService.getAccount(this.account.id);
      this.initDocMap();

      this.alertMessager.showToast(undefined, 'Document deleted successfully!', 'success');
      await loader.dismiss();
    } catch (err) {
      console.log(err);
      this.alertMessager.showToast(undefined, 'Document could not be deleted', 'danger');
      await loader.dismiss();
    }
  }

  async initDocMap() {
    this.displayDocumentsMap.clear();

    const filters = new SearchDocumentsRequest();
    filters.orgIds = [this.orgId];
    filters.accountIds = [this.account.id];

    const accountDocs = await this.documentService.generateAllAccountsDocuments(filters);

    for (const doc of accountDocs) {
      const isTemplateDoc = await this.isTemplateDoc(doc);

      if (isTemplateDoc) {
        if (!this.displayDocumentsMap.has(this.account.id)) {
          this.displayDocumentsMap.set(this.account.id, {
            showTitle: this.orgId === 58 ? true : false,
            title: 'Other',
            org: undefined,
            requiredDocs: [],
            showDocs: true,
            missingDocs: [],
            uploadedDocs: []
          });
        }

        const mapObject = this.displayDocumentsMap.get(this.account.id);

        if (doc.uploadedOn) {
          mapObject.uploadedDocs.push(doc);
        } else {
          mapObject.missingDocs.push(doc);
        }
      } else {
        const requiredDoc = await this.documentService.getRequiredDocument(doc.organizationDocumentId);

        if (!this.displayDocumentsMap.has(requiredDoc.organizationId)) {
          this.displayDocumentsMap.set(requiredDoc.organizationId, {
            showTitle: this.orgId === 58 ? true : false,
            title: requiredDoc.organization.name,
            org: requiredDoc.organization,
            requiredDocs: [requiredDoc],
            showDocs: true,
            missingDocs: [],
            uploadedDocs: []
          });
        }

        const mapObject = this.displayDocumentsMap.get(requiredDoc.organizationId);
        if (!mapObject.requiredDocs.some(x => x.id === requiredDoc.id)) { 
          mapObject.requiredDocs.push(requiredDoc);
        }

        if (doc.uploadedOn) {
          mapObject.uploadedDocs.push(doc);
        } else {
          mapObject.missingDocs.push(doc);
        }
      }
    }
  }

  async isTemplateDoc(document: AccountDocument) {
    if (document.documentType !== null && (document.documentType === DocumentType.BirthCertificate || document.documentType === DocumentType.TaxDocument)) {
      return true;
    }

    return false;
  }

  async showDocPreview(document: OrganizationRequiredDocument) {
    if (this.popoverActive) {
      return;
    }
    this.popoverActive = true;

    const modal = await this.modalCtrl.create({
      component: SecureDocumentPopoverComponent,
      componentProps: {
        isViewOnly: true,
        fileUrl: document.url,
        fileName: document.name,
      },
    })

    await modal.present();

    await modal.onDidDismiss().then(() => {
      this.popoverActive = false;
    })
  }
}
